import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useAdminUsers, useUpdateUser, useDeleteUser, useFreezeUser, useUnfreezeUser, useBanUser, useUnbanUser } from '../api/useAdmin'
import { useAuthStore } from '../store/authStore'
import { useClickOutside } from '../hooks/useClickOutside'
import ConfirmModal from '../components/ui/ConfirmModal'
import Pagination from '../components/ui/Pagination'
import toast from 'react-hot-toast'

const STATUS_LABEL = { active: 'Активен', frozen: 'Заморожен', banned: 'Забанен' }
const STATUS_CSS = { active: 'text-green-400 bg-green-400/10', frozen: 'text-blue-400 bg-blue-400/10', banned: 'text-red-400 bg-red-400/10' }

export default function AdminUsers() {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const { data, isLoading } = useAdminUsers(page, q)
  const currentUser = useAuthStore((s) => s.user)

  const items = data?.items || []
  const pages = data?.pages || 1
  const total = data?.total || 0

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-sm text-muted hover:text-accent transition-colors">← Админ</Link>
        <span className="text-line">/</span>
        <h1 className="text-xl font-bold">Пользователи</h1>
        {total > 0 && <span className="text-sm text-muted">{total}</span>}
      </div>

      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setPage(1) }}
        placeholder="Поиск по имени или email..."
        className="w-full mb-5 px-4 py-2 rounded-xl text-sm outline-none bg-surface border border-line text-fg focus:border-accent transition-colors placeholder:text-muted"
      />

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl animate-pulse bg-surface" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-center py-16 text-muted">Пользователей нет</p>
      ) : (
        <>
          <div className="rounded-2xl overflow-hidden border border-line">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface border-b border-line">
                  <th className="text-left px-4 py-3 font-semibold text-muted">Пользователь</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted hidden sm:table-cell">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted">Роль</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted hidden lg:table-cell">Статус</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted hidden md:table-cell">Регистрация</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((user, i) => (
                  <UserRow key={user.id} user={user} isSelf={user.id === currentUser?.id} last={i === items.length - 1} />
                ))}
              </tbody>
            </table>
          </div>

          <Pagination page={page} total={pages} onChange={setPage} />
        </>
      )}
    </div>
  )
}

function UserRow({ user, isSelf, last }) {
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [actionModal, setActionModal] = useState(null) // { type: 'freeze'|'ban', reason: '', days: '1' }
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 })
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const { mutate: updateUser, isPending: updating } = useUpdateUser(user.id)

  useClickOutside(menuRef, () => setMenuOpen(false))

  const openMenu = () => {
    const rect = btnRef.current.getBoundingClientRect()
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    setMenuOpen(true)
  }
  const { mutate: deleteUser, isPending: deleting } = useDeleteUser()
  const { mutate: freeze, isPending: freezing } = useFreezeUser()
  const { mutate: unfreeze, isPending: unfreezing } = useUnfreezeUser()
  const { mutate: ban, isPending: banning } = useBanUser()
  const { mutate: unban, isPending: unbanning } = useUnbanUser()

  const status = user.status || 'active'
  const dateStr = new Date(user.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })

  const toggleRole = () => {
    const newRole = user.role === 'admin' ? 'user' : 'admin'
    updateUser({ role: newRole }, {
      onSuccess: () => toast.success(`Роль изменена на ${newRole}`),
      onError: () => toast.error('Ошибка при изменении роли'),
    })
  }

  const handleFreeze = () => {
    if (!actionModal?.reason?.trim()) { toast.error('Укажите причину'); return }
    let until = null
    if (actionModal.days !== 'forever') {
      const d = new Date()
      d.setDate(d.getDate() + Number(actionModal.days))
      until = d.toISOString()
    }
    freeze({ userId: user.id, reason: actionModal.reason, until }, {
      onSuccess: () => { toast.success('Аккаунт заморожен'); setActionModal(null) },
      onError: () => toast.error('Ошибка'),
    })
  }

  const handleBan = () => {
    if (!actionModal?.reason?.trim()) { toast.error('Укажите причину'); return }
    ban({ userId: user.id, reason: actionModal.reason }, {
      onSuccess: () => { toast.success('Аккаунт заблокирован'); setActionModal(null) },
      onError: () => toast.error('Ошибка'),
    })
  }

  const handleUnfreeze = () => {
    unfreeze(user.id, {
      onSuccess: () => toast.success('Аккаунт разморожен'),
      onError: () => toast.error('Ошибка'),
    })
  }

  const handleUnban = () => {
    unban(user.id, {
      onSuccess: () => toast.success('Аккаунт разблокирован'),
      onError: () => toast.error('Ошибка'),
    })
  }

  return (
    <>
      <tr className={`bg-surface hover:bg-surface2 transition-colors ${!last ? 'border-b border-line' : ''}`}>
        <td className="px-4 py-3">
          <span className="font-medium">{user.username}</span>
          {isSelf && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-accent-dim text-accent">вы</span>}
        </td>
        <td className="px-4 py-3 hidden sm:table-cell text-muted">{user.email}</td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${user.role === 'admin' ? 'bg-accent-dim text-accent' : 'bg-surface2 text-muted'}`}>
            {user.role === 'admin' ? 'Админ' : 'Пользователь'}
          </span>
        </td>
        <td className="px-4 py-3 hidden lg:table-cell">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CSS[status] || STATUS_CSS.active}`}>
            {STATUS_LABEL[status] || status}
          </span>
        </td>
        <td className="px-4 py-3 hidden md:table-cell text-sm text-muted">{dateStr}</td>
        <td className="px-4 py-3">
          {!isSelf && (
            <div className="flex justify-end">
              <button
                ref={btnRef}
                onClick={openMenu}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-muted hover:text-fg hover:bg-surface2 transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {menuOpen && createPortal(
                <div
                  ref={menuRef}
                  style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
                  className="w-44 rounded-xl py-1 shadow-2xl z-[200] bg-surface border border-line"
                >
                  <button onClick={() => { toggleRole(); setMenuOpen(false) }} disabled={updating}
                    className="w-full text-left px-4 py-2 text-sm text-fg hover:bg-surface2 transition-colors disabled:opacity-50">
                    {user.role === 'admin' ? 'Сменить на user' : 'Сменить на admin'}
                  </button>
                  <div className="border-t border-line my-1" />
                  {status === 'active' && (<>
                    <button onClick={() => { setActionModal({ type: 'freeze', reason: '', days: '1' }); setMenuOpen(false) }}
                      className="w-full text-left px-4 py-2 text-sm text-blue-400 hover:bg-surface2 transition-colors">
                      🧊 Заморозить
                    </button>
                    <button onClick={() => { setActionModal({ type: 'ban', reason: '' }); setMenuOpen(false) }}
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-surface2 transition-colors">
                      🚫 Забанить
                    </button>
                  </>)}
                  {status === 'frozen' && (
                    <button onClick={() => { handleUnfreeze(); setMenuOpen(false) }} disabled={unfreezing}
                      className="w-full text-left px-4 py-2 text-sm text-blue-400 hover:bg-surface2 transition-colors disabled:opacity-50">
                      Разморозить
                    </button>
                  )}
                  {status === 'banned' && (
                    <button onClick={() => { handleUnban(); setMenuOpen(false) }} disabled={unbanning}
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-surface2 transition-colors disabled:opacity-50">
                      Разбанить
                    </button>
                  )}
                  <div className="border-t border-line my-1" />
                  <button onClick={() => { setDeleteConfirm(true); setMenuOpen(false) }}
                    className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-surface2 transition-colors">
                    Удалить
                  </button>
                </div>,
                document.body
              )}
            </div>
          )}
        </td>
      </tr>

      <ConfirmModal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={() => {
          deleteUser(user.id, {
            onSuccess: () => toast.success('Пользователь удалён'),
            onError: () => toast.error('Ошибка при удалении'),
          })
          setDeleteConfirm(false)
        }}
        title={`Удалить «${user.username}»?`}
        message="Аккаунт будет удалён безвозвратно вместе со всеми данными."
        confirmLabel="Да, удалить"
        isPending={deleting}
        danger
      />

      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setActionModal(null)}>
          <div className="w-full max-w-sm bg-canvas rounded-2xl p-6 border border-line shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">
              {actionModal.type === 'freeze' ? '🧊 Заморозить аккаунт' : '🚫 Заблокировать аккаунт'}
            </h2>
            <p className="text-sm text-muted mb-4">{user.username}</p>
            <label className="text-xs font-semibold text-muted mb-1 block">Причина</label>
            <textarea
              value={actionModal.reason}
              onChange={(e) => setActionModal((v) => ({ ...v, reason: e.target.value }))}
              placeholder="Причина для пользователя..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none bg-surface border border-line text-fg focus:border-accent transition-colors resize-none mb-3"
            />
            {actionModal.type === 'freeze' && (
              <>
                <label className="text-xs font-semibold text-muted mb-1 block">Срок</label>
                <select
                  value={actionModal.days}
                  onChange={(e) => setActionModal((v) => ({ ...v, days: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none bg-surface border border-line text-fg focus:border-accent transition-colors mb-4"
                >
                  <option value="1">1 день</option>
                  <option value="3">3 дня</option>
                  <option value="7">1 неделя</option>
                  <option value="14">2 недели</option>
                  <option value="30">1 месяц</option>
                  <option value="forever">Навсегда</option>
                </select>
              </>
            )}
            {actionModal.type !== 'freeze' && <div className="mb-4" />}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setActionModal(null)}
                className="px-4 py-2 rounded-lg text-sm text-muted hover:text-fg transition-colors">
                Отмена
              </button>
              <button
                onClick={actionModal.type === 'freeze' ? handleFreeze : handleBan}
                disabled={freezing || banning}
                className={`px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 ${actionModal.type === 'ban' ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'} transition-colors`}>
                {actionModal.type === 'freeze' ? 'Заморозить' : 'Заблокировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
